export enum LoggerLevel {
  OFF = 0,
  ERROR = 1,
  WARN = 2,
  INFO = 3,
  DEBUG = 4,
  VERBOSE = 5,
}
export interface Logger {
  /**
   * Based on the loglevel [{@link LoggerLevel}], information to be viewed on the console
   * will be filtered.
   * Default value is LoggerLevel.DEBUG.
   */
  readonly logLevel: LoggerLevel;
  /**
   * Logs the number of times that this particular call to count() has been called.
   * This function takes an optional argument label.
   *
   * @property countTitle  optional
   * If provided, `count()` outputs the number of times it has been called with that label.
   * If omitted, `count()` behaves as though it was called with the "default" label.
   */
  count(countTitle?: string): void;

  /**
   * Resets the counter. This function takes an optional argument label.
   *
   * @property countTitle  optional
   * If provided, `countReset()` resets the count for that label to 0.
   * If omitted, `countReset()` resets the count for that label to 0.
   */
  countReset(countTitle?: string): void;

  /**
   * The Console method debug() outputs a message to the web console at the "debug" log level.
   * The message is only displayed to the user if the console is configured to display debug output
   *
   * The `console.debug()` function is an alias for {@link console.log()}.
   */

  debug(message?: any, ...optionalParams: any[]): void;
  /**
   * Prints to `stderr` with newline.
   * The message may be a single string (with optional substitution values), or it may be any one or more objects
   * @property message  optional
   * @property optionalParams  optional
   */
  error(message?: any, ...optionalParams: any[]): void;
  /**
   * Outputs an informational message to the Web Console
   * The message may be a single string (with optional substitution values), or it may be any one or more objects
   * @property message  optional
   * @property optionalParams  optional {@link console.info()}
   */
  info(message?: any, ...optionalParams: any[]): void;
  /**
   * Prints to `stdout` with newline.
   *
   * The Console method log() outputs a message to the web console.
   * The message may be a single string (with optional substitution values), or it may be any one or more objects
   * @property message  optional
   * @property optionalParams  optional
   */
  log(message?: any, ...optionalParams: any[]): void;

  /**
   * Starts a timer you can use to track how long an operation takes. You give each timer a unique name,
   * and may have up to 10,000 timers running on a given page. When you call `timeEnd()` with the same name,
   * the browser will output the time, in milliseconds, that elapsed since the timer was started.
   *
   * @property label  optional
   * The name to give the new timer. This will identify the timer; use the same name when calling
   * {@link console.timeEnd()}  to stop the timer and get the time output to the console
   */
  time(timerName?: string): void;

  /**
   * Stops a timer that was previously started by calling {@link console.time()} and prints the result.
   *
   * @property label  optional
   * The name of the timer to stop. Once stopped, the elapsed time is automatically displayed in the
   * Console along with an indicator that the time has ended.
   */
  timeEnd(timerName?: string): void;

  /**
   * Outputs a warning message
   *
   * @property message  optional
   * @property optionalParams  optional
   * same as {@link console.warn()}
   */
  warn(message?: any, ...optionalParams: any[]): void;
}
